"use client";

/**
 * The "free pin cap reached" Pro upsell. Mounted once at the app root and
 * driven by the global use-pin-limit store, so every save path (listing-card
 * bookmark, My-Map editor) shows the same prompt.
 *
 * DESKTOP: a vertically-centred modal (scale + fade in).
 * MOBILE:  a sheet rising from the bottom.
 * Both share a white header with a Crown (the quiet UI, 2026-10-29: no
 * brand-filled surfaces), the Pro benefits, and the one indigo CTA. Portal'd to <body> so no ancestor transform mis-anchors
 * the fixed positioning.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Link from "@/lib/localized-link";
import { Crown, Check, X, LogIn } from "lucide-react";
import { useT } from "@/lib/locale-context";
import { type TranslationKey } from "@/lib/i18n";
import { usePinLimit, type PinLimitKind } from "@/lib/use-pin-limit";
import { useAuthHref } from "@/lib/use-return-to";

// Copy + manage-link per cap kind. Same sheet chrome, different strings.
const COPY: Record<
  PinLimitKind,
  {
    eyebrow: TranslationKey;
    title: TranslationKey;
    body: TranslationKey;
    benefits: readonly TranslationKey[];
    pro: TranslationKey;
    manageHref: string;
    manageLabel: TranslationKey;
  }
> = {
  pins: {
    eyebrow: "mymap.limit.eyebrow",
    title: "mymap.limit.title",
    body: "mymap.limit.body",
    benefits: ["mymap.limit.benefit.unlimited", "mymap.limit.benefit.organize", "mymap.limit.benefit.track"],
    pro: "mymap.limit.pro",
    manageHref: "/watchlist",
    manageLabel: "mymap.limit.manage",
  },
  properties: {
    eyebrow: "myproperties.limit.eyebrow",
    title: "myproperties.limit.title",
    body: "myproperties.limit.body",
    benefits: ["myproperties.limit.benefit.unlimited", "myproperties.limit.benefit.yield", "myproperties.limit.benefit.portfolio"],
    pro: "myproperties.limit.cta",
    manageHref: "/properties",
    manageLabel: "myproperties.title",
  },
  // Device-bound saving hit its ladder (a few hearts, one search per
  // browser) or the browser refused push: the ask is an account, and
  // the primary action is sign-in, not Pro.
  follow: {
    eyebrow: "residence.follow.sheet.eyebrow",
    title: "residence.follow.sheet.title",
    body: "residence.follow.sheet.body",
    benefits: ["residence.follow.benefit_new", "residence.follow.benefit_drop", "residence.follow.sheet.benefit_channels"],
    pro: "watchlist.anon.signin",
    manageHref: "/residences",
    manageLabel: "watchlist.anon.later",
  },
  account: {
    eyebrow: "watchlist.anon.eyebrow",
    title: "watchlist.anon.title",
    body: "watchlist.anon.body",
    benefits: ["watchlist.anon.benefit.devices", "watchlist.anon.benefit.channels", "watchlist.anon.benefit.free"],
    pro: "watchlist.anon.signin",
    manageHref: "/listings",
    manageLabel: "watchlist.anon.later",
  },
};

export default function PinLimitSheet() {
  const t = useT();
  const { limit, kind, close } = usePinLimit();
  const open = limit !== null;
  const copy = COPY[kind];
  const loginHref = useAuthHref("login");
  const accountKind = kind === "account" || kind === "follow";
  // The follow sheet comes back to the residence page with ?follow=1 so
  // the page finishes the follow the moment the session exists.
  const primaryHref = kind === "follow"
    ? loginHref.replace(/return_to=([^&]*)/, (_, v) => {
        const path = decodeURIComponent(v);
        return "return_to=" + encodeURIComponent(path + (path.includes("?") ? "&" : "?") + "follow=1");
      })
    : accountKind ? loginHref : "/pricing";

  // render/entered lifecycle so it animates in AND out.
  const [render, setRender] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (open) {
      setRender(true);
      return;
    }
    setEntered(false);
    const id = setTimeout(() => setRender(false), 240);
    return () => clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!render || !open) return;
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [render, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close]);

  if (!render || typeof document === "undefined") return null;

  const benefits = copy.benefits.map((k) => t(k));

  return createPortal(
    <>
      <div
        onClick={close}
        aria-hidden="true"
        style={{ opacity: entered ? 1 : 0, transition: "opacity 240ms ease-out" }}
        className="fixed inset-0 z-[1300] bg-gray-950/60 backdrop-blur-sm"
      />
      {/* Mobile: bottom (items-end). Desktop: centred (sm:items-center). */}
      <div className="pointer-events-none fixed inset-0 z-[1310] flex items-end justify-center sm:items-center sm:p-4">
        <div
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
          className={`pointer-events-auto w-full overflow-hidden rounded-t-3xl bg-white shadow-2xl transition-all duration-300 ease-out sm:w-[27rem] sm:max-w-[calc(100vw-2rem)] sm:rounded-3xl ${
            entered
              ? "translate-y-0 opacity-100 sm:scale-100"
              : "translate-y-full opacity-0 sm:translate-y-0 sm:scale-95"
          }`}
        >
          {/* Header: white, the crown in a grey tile. */}
          <div className="relative border-b border-gray-100 px-6 pb-6 pt-6">
            <button
              type="button"
              onClick={close}
              aria-label={t("listings.detail.close")}
              className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-100 text-gray-700">
              <Crown className="h-6 w-6" />
            </div>
            <div className="relative mt-3.5 inline-flex items-center gap-1.5 text-[11px] font-bold text-gray-500">
              {t(copy.eyebrow)}
            </div>
            <h3 className="relative mt-1 text-xl font-extrabold leading-tight text-gray-900">
              {t(copy.title).replace("{limit}", String(limit ?? ""))}
            </h3>
            <p className="relative mt-1.5 text-sm leading-relaxed text-gray-600">
              {t(copy.body)}
            </p>
          </div>

          {/* Benefits + CTAs. */}
          <div className="px-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-5 sm:pb-6">
            <ul className="space-y-3">
              {benefits.map((b) => (
                <li key={b} className="flex items-center gap-3 text-sm font-medium text-gray-700">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-700">
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
            <div className="mt-6 flex flex-col gap-2.5">
              <Link
                href={primaryHref}
                onClick={close}
                className={
                  accountKind
                    ? "flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-3.5 text-sm font-bold text-white transition-colors hover:bg-indigo-700"
                    : "flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-3.5 text-sm font-bold text-white shadow-lg transition hover:bg-indigo-700"
                }
              >
                {accountKind ? <LogIn className="h-4 w-4" /> : <Crown className="h-4 w-4" />}
                {t(copy.pro)}
              </Link>
              <Link
                href={copy.manageHref}
                onClick={close}
                className="text-center text-sm font-semibold text-gray-500 hover:text-gray-700"
              >
                {t(copy.manageLabel)}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
